import _extends from '@babel/runtime/helpers/extends';
import React from 'react';
import PropTypes from 'prop-types';
import { p as props, d as defaults } from './componentEl-c8b73b10.js';
import { a as addClassNames, c as classNamePropTypes } from './classNames-cc2dcbf1.js';
import { a as addSpacingClasses, m as marginPropTypes, p as paddingPropTypes } from './spacing-7e57ffb0.js';
import { a as addBorderClasses, b as borderPropTypes } from './border-27b45cc2.js';

function GroupItem({
  children,
  componentEl: Component,
  flex,
  margin,
  padding,
  className,
  fullWidth,
  align,
  justify,
  grow,
  shrink,
  border,
  // eslint-disable-next-line react/prop-types
  style,
  ...otherProps
}) {
  let cssClasses = ['st_grp__item'];
  if (fullWidth) cssClasses.push('st_width--full');
  if (flex) cssClasses.push('st_grp__item--flex');
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    margin,
    cssClasses
  });
  if (align) cssClasses.push(`st_alignself--${align}`);
  if (justify) cssClasses.push(`st_justifyself--${justify}`);
  const styleProp = style || {};
  styleProp['--st-flexgrow'] = grow;
  styleProp['--st-flexshrink'] = shrink;
  cssClasses = addBorderClasses(border, cssClasses);
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' '),
    style: styleProp
  }, otherProps), children);
}
GroupItem.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** margin for component, if any */
  margin: marginPropTypes,
  /** padding for component, if any */
  padding: paddingPropTypes,
  /** whether item should take up 100% of available width */
  fullWidth: PropTypes.bool,
  /** item takes up rest of available space */
  flex: PropTypes.bool,
  /** align-self CSS property */
  align: PropTypes.oneOf(['flex-start', 'flex-end', 'center', 'baseline', 'stretch']),
  /** justify-self CSS property */
  justify: PropTypes.oneOf(['flex-start', 'flex-end', 'center', 'baseline', 'stretch']),
  /** flex-grow css property */
  grow: PropTypes.number,
  /** flex-shrink css property */
  shrink: PropTypes.number,
  /** whether column should show a border to the left or right */
  border: borderPropTypes
};
GroupItem.defaultProps = {
  componentEl: defaults,
  className: null,
  margin: null,
  padding: null,
  fullWidth: false,
  flex: false,
  align: null,
  justify: null,
  grow: 0,
  shrink: 0,
  border: null
};
GroupItem.displayName = 'Group.Item';

function Group({
  children,
  componentEl: Component,
  align,
  valign,
  direction,
  margin,
  inline,
  className,
  gap,
  colgap,
  rowgap,
  wrap,
  fullWidth,
  fullHeight,
  border,
  componentRef,
  ...otherProps
}) {
  let cssClasses = ['st_grp', `st_grp--${direction}`, `st_grp--a-${align}`, `st_grp--v-${valign}`];
  cssClasses = addBorderClasses(border, cssClasses);
  if (gap && !rowgap && !colgap) cssClasses.push(`st_grp--g-${gap}`);
  if (rowgap) cssClasses.push(`st_grp--gr-${rowgap}`);
  if (colgap) cssClasses.push(`st_grp--gc-${colgap}`);
  if (fullWidth) cssClasses.push('st_width--full');
  if (fullHeight) cssClasses.push('st_height--full');
  if (inline) cssClasses.push('st_grp--inline');
  if (wrap) cssClasses.push('st_grp--wrap');
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    margin,
    cssClasses
  });
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps, {
    ref: componentRef
  }), children);
}
Group.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** ref for element */
  componentRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })]),
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** whether to display flex inline */
  inline: PropTypes.bool,
  /** margin for component, if any */
  margin: marginPropTypes,
  /** direction of items. Row = horizontal, Column = vertical */
  direction: PropTypes.oneOf(['row', 'column', 'row-reverse', 'column-reverse']),
  /** how to align items horizontally */
  align: PropTypes.oneOf(['center', 'start', 'end', 'stretch', 'space-between', 'initial']),
  /** space between items, if any */
  gap: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl', 'none']),
  /** space between columns, if not set via `gap` */
  colgap: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl', 'none']),
  /** space between rows, if not set via `gap` */
  rowgap: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl', 'none']),
  /** how to align items vertically */
  valign: PropTypes.oneOf(['baseline', 'center', 'start', 'end', 'stretch', 'initial']),
  /** only used in row mode. Whether to wrap if not enough horizontal space */
  wrap: PropTypes.bool,
  /** whether component takes up 100% of available width */
  fullWidth: PropTypes.bool,
  /** whether component takes up 100% of available height */
  fullHeight: PropTypes.bool,
  /** optional standard border around component */
  border: borderPropTypes
};
Group.defaultProps = {
  componentEl: defaults,
  inline: false,
  className: null,
  margin: null,
  direction: 'row',
  align: 'start',
  valign: 'center',
  gap: 'mlg',
  wrap: true,
  colgap: null,
  rowgap: null,
  fullWidth: null,
  fullHeight: null,
  border: null,
  componentRef: null
};
Group.Item = GroupItem;

export { Group as G };
